import { getCsrfFromCookies } from '@/api/cookies'
import { getPusherService } from '@/api/pusher'
import { Sentry, configureSentry } from '@/api/sentry'
import { useCopy } from '@/api/useCopy'
import { useDateTime } from '@/api/useDateTime'
import { useStackedModals } from '@/api/useModal'
import { usePanel } from '@/api/usePanel'
import { usePermissions } from '@/api/usePermissions'
import { useToast } from '@/api/useToast'
import Image from '@/components/Image.vue'
import Alert from '@/components/ui/Alert.vue'
import Card from '@/components/ui/Card.vue'
import Empty from '@/components/ui/Empty.vue'
import Icon from '@/components/ui/Icon.vue'
import TButton from '@/components/ui/TButton.vue'
import TCheckbox from '@/components/ui/TCheckbox.vue'
import TCheckboxGroup from '@/components/ui/TCheckboxGroup.vue'
import TInput from '@/components/ui/TInput.vue'
import TRadioGroup from '@/components/ui/TRadioGroup.vue'
import TSelect from '@/components/ui/TSelect.vue'
import TSwitch from '@/components/ui/TSwitch.vue'
import TTextarea from '@/components/ui/TTextarea.vue'
import Layout from '@/layouts/BaseLayout.vue'
import VueCustomTooltip from '@adamdehaven/vue-custom-tooltip'
import { Head, Link, createInertiaApp, usePage } from '@inertiajs/vue3'
import { createApp, h } from 'vue'
import VueLazyLoad from 'vue3-lazyload'

import version from '@/api/version'
import 'v-calendar/dist/style.css'
import '../../index.css'

import FilterOffIcon from '@/components/icons/FilterOffIcon.vue'
import HubspotIcon from '@/components/icons/HubspotIcon.vue'
import DriveIcon from '@/components/icons/DriveIcon.vue'
import LoadingIcon from '@/components/icons/LoadingIcon.vue'
import MaskIcon from '@/components/icons/MaskIcon.vue'
import SolidSaveIcon from '@/components/icons/SolidSaveIcon.vue'
import TrophyIcon from '@/components/icons/TrophyIcon.vue'
import * as outlineIcons from '@heroicons/vue/outline'
import * as solidIcons from '@heroicons/vue/solid'

const pages = import.meta.glob('./pages/**/*.vue')

createInertiaApp({
  progress: {
    // The color of the request progress bar.
    color: '#2469FF',
  },
  resolve: (name) => {
    // [2]
    return pages[`./pages/${name}.vue`]().then((module) => {
      const page = module.default
      console.log('resolve', page.layout)
      page.layout = page.layout || Layout
      return module.default
    })
  },
  title: (title) => `${title} | Archibien`,
  setup({ el, App, props, plugin }) {
    const vueApp = createApp({
      data() {
        return {
          connection: null,
          notificationsAbsCount: 0,
          hasNewNotifications: false,
          // eslint-disable-next-line vue/no-reserved-keys
          _notifications: [],
          version,
          moneyFormatter: new Intl.NumberFormat('fr-FR', {
            minimumFractionDigits: 2,
          }),
        }
      },
      computed: {
        notificationsCount() {
          if (this.notificationsAbsCount == 0) {
            return ''
          } else if (this.notificationsAbsCount > 99) {
            return '+99'
          } else {
            return this.notificationsAbsCount.toString()
          }
        },
      },
      watch: {
        notificationsAbsCount: {
          handler(newVal, oldVal) {
            this.hasNewNotifications = newVal - oldVal >= 1
          },
          deep: true,
          immediate: false,
        },
      },
      mounted() {
        const user = usePage().props.user
        if (user) {
          this.notificationsAbsCount = parseInt(usePage().props.notifications_count)

          if (this.hasRole('manager')) {
            const pusher = getPusherService(getCsrfFromCookies())
            let channel = pusher.subscribe(`private-${user.id}`)
            channel.bind('new-notification', (data) => {
              this.showNotification(data)
            })
            channel = pusher.subscribe('private-general')
            channel.bind('update', (data) => {
              this.showMessage(data)
            })
          }

          this.startCheckingForRefresh()

          // add user to Sentry
          Sentry.setUser({
            id: user.id,
            username: user.full_name,
            email: user.email,
          })
        }
      },
      methods: {
        startCheckingForRefresh() {
          this.hasNewNotifications = false
        },
        refreshPage() {
          this.hasNewNotifications = false
          this.$inertia.reload()
        },
        decrementNotifications() {
          if (this.notificationsAbsCount == 0) {
            return
          }
          this.notificationsAbsCount -= 1
        },
        removeNotification(id) {
          const index = this._notifications.indexOf((n) => n.id === id)
          this._notifications.splice(index, 1)
        },
        showNotification(notif) {
          this._notifications.push(notif)
          this.notificationsAbsCount += 1
          setTimeout(() => {
            this.removeNotification(notif.id)
          }, 3000)
        },
        showMessage(notif) {
          this._notifications.push(notif)
        },
        formatMoney(value) {
          return this.moneyFormatter.format(value)
        },
      },
      render: () => h(App, props),
    })
      .use(plugin)
      .use(VueCustomTooltip, {
        name: 'VueCustomTooltip',
        color: '#fff',
        background: '#000',
        borderRadius: 10,
        fontWeight: 400,
      })
      .use(VueLazyLoad)
      .component('Link', Link)
      .component('Head', Head)
      .component('Alert', Alert)
      .component('Card', Card)
      .component('TInput', TInput)
      .component('TButton', TButton)
      .component('TTextarea', TTextarea)
      .component('TRadioGroup', TRadioGroup)
      .component('TCheckbox', TCheckbox)
      .component('TCheckboxGroup', TCheckboxGroup)
      .component('TSwitch', TSwitch)
      .component('TSelect', TSelect)
      .component('Icon', Icon)
      .component('LoadingIcon', LoadingIcon)
      .component('FilterOffIcon', FilterOffIcon)
      .component('MaskIcon', MaskIcon)
      .component('HubspotIcon', HubspotIcon)
      .component('DriveIcon', DriveIcon)
      .component('TrophyIcon', TrophyIcon)
      .component('Image', Image)
      .component('Empty', Empty)
      .mixin(useStackedModals)
      .mixin(usePanel)
      .mixin(useCopy)
      .mixin(useDateTime)
      .mixin(usePermissions)
      .provide('$toast', useToast)
    vueApp.config.globalProperties.$toast = useToast

    Object.entries(outlineIcons).forEach((entry) => {
      vueApp.component(entry[0], outlineIcons[entry[0]])
    })
    Object.entries(solidIcons).forEach((entry) => {
      if (entry[0] !== 'SaveIcon') {
        vueApp.component(`Solid${entry[0]}`, solidIcons[entry[0]])
      }
    })
    // use our own icon for save
    vueApp.component('SolidSaveIcon', SolidSaveIcon)

    configureSentry(vueApp)
    vueApp.mount(el)
  },
})
